import { useState, useEffect, FormEvent } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const name = formData.get('name')?.toString().trim() ?? '';
    const email = formData.get('email')?.toString().trim() ?? '';
    const phone = formData.get('phone')?.toString().trim() ?? '';
    const address = formData.get('address')?.toString().trim() ?? '';

    if (!name || !email || !phone || !address) return;

    setSubmitStatus('sending');

    try {
      const response = await fetch('https://readdy.ai/api/form/d6664ukn78sauj0em730', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (!response.ok) throw new Error(`Server error: ${response.status}`);

      setSubmitStatus('success');
      form.reset();
      setTimeout(() => setSubmitStatus('idle'), 5000);
    } catch (err) {
      console.error('Form submission failed:', err);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 3000);
    }
  };

  const manufacturers = [
    { name: 'Choshu Industry', logo: 'https://static.readdy.ai/image/433d85233a05655d3e1b986bbaebb8d0/2665f165470410885b4712fb786ebecc.jpeg' },
    { name: 'XSOL', logo: 'https://static.readdy.ai/image/433d85233a05655d3e1b986bbaebb8d0/66b4bdb403f81ed09572269b27db544f.webp' },
    { name: 'Canadian Solar', logo: 'https://static.readdy.ai/image/433d85233a05655d3e1b986bbaebb8d0/e04e0541d2470df6db453128d006b87c.png' },
    { name: 'Kyocera', logo: 'https://static.readdy.ai/image/433d85233a05655d3e1b986bbaebb8d0/53738ee2ab7fe7be706e727ddda53bb5.png' },
  ];

  const advantages = [
    {
      icon: 'ri-shield-check-line',
      title: '雨漏りゼロ保証',
      subtitle: '屋根構造の専門知識',
      description: '屋根リフォームの専門家として、構造を熟知。下地から理解しているからこそできる施工品質。施工起因の雨漏りは、10年間責任保証します。',
    },
    {
      icon: 'ri-line-chart-line',
      title: '精密シミュレーション',
      subtitle: 'ENEがえる搭載',
      description: '高精度な「ENEがえる」シミュレーション技術を使用。太陽光発電がお住まいに適さない場合は、正直にお伝えします。強引な営業は一切ありません。',
    },
    {
      icon: 'ri-tools-line',
      title: 'ワンストップサービス',
      subtitle: '太陽光&蓄電池＋リフォーム',
      description: '太陽光発電と屋根リフォーム・塗装を同時に行うことで、足場は一度、工事も一度。無駄な工程を省き、工期を短縮。コストを抑えながら、住まいの耐久性と発電効率を同時に高めます。',
    },
  ];

  const testimonials = [
    {
      name: '指原様ご家族',
      location: '世田谷区',
      image: 'https://public.readdy.ai/ai/img_res/d9f0e6583eb5619231bc63e47289e348.jpg',
      quote: '雨漏りが心配でしたが、まごころさんの専門知識で安心できました。電気代が70%も削減できました！',
      rating: 5,
    },
    {
      name: '三崎様ご家族',
      location: '目黒区',
      image: 'https://readdy.ai/api/search-image?query=smiling%20Japanese%20family%20with%20two%20children%20in%20front%20of%20eco-friendly%20home%20with%20solar%20installation%2C%20green%20garden%20background%2C%20casual%20weekend%20attire%2C%20natural%20lighting%2C%20lifestyle%20photography&width=400&height=400&seq=testimonial-002&orientation=squarish',
      quote: 'シミュレーションが非常に正確でした。屋根の形状に合わせた最適なパネル配置もアドバイスしてくれました。',
      rating: 5,
    },
    {
      name: '山本様ご家族',
      location: '品川区',
      image: 'https://readdy.ai/api/search-image?query=elderly%20Japanese%20couple%20looking%20satisfied%20and%20happy%2C%20standing%20near%20modern%20Japanese%20house%20exterior%2C%20warm%20afternoon%20light%2C%20comfortable%20casual%20clothing%2C%20genuine%20expressions%2C%20documentary%20style%20photography&width=400&height=400&seq=testimonial-003&orientation=squarish',
      quote: '太陽光と屋根塗装を同時に行い、大幅にコスト削減できました。チームはプロフェッショナルで丁寧でした。',
      rating: 5,
    },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="w-full px-4 sm:px-6 lg:px-12">
          <div className="flex justify-between items-center h-18 py-4">
            <div className="flex items-center space-x-3">
              <img
                src="https://static.readdy.ai/image/433d85233a05655d3e1b986bbaebb8d0/93db42b0d420b42c689b29e03900a394.png"
                alt="まごころ工務店"
                className="h-12 w-auto"
              />
            </div>

            <div className="hidden lg:flex items-center space-x-8">
              {[
                { label: '選ばれる理由', id: 'advantage' },
                { label: 'シミュレーション', id: 'simulation' },
                { label: '取扱メーカー', id: 'brands' },
                { label: 'ハイブリッドプラン', id: 'hybrid' },
                { label: 'キャンペーン', id: 'campaign' },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-600 hover:text-[#8BC34A]' : 'text-white/90 hover:text-white'}`}
                >
                  {item.label}
                </button>
              ))}
              <button
                onClick={() => scrollToSection('contact')}
                className="text-sm font-medium text-white px-6 py-2.5 rounded-lg transition-all whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl"
                style={{ background: 'linear-gradient(135deg, #8BC34A 0%, #00BCD4 100%)' }}
              >
                無料シミュレーション
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=stunning%20aerial%20view%20of%20beautiful%20modern%20Japanese%20residential%20home%20in%20Tokyo%20with%20premium%20solar%20panels%20installed%20on%20clean%20roof%2C%20bright%20sunny%20day%20with%20clear%20blue%20sky%2C%20lush%20green%20trees%20surrounding%20the%20property%2C%20professional%20architectural%20photography%2C%20warm%20natural%20lighting%2C%20high-end%20real%20estate%20style&width=1920&height=1080&seq=hero-main-tokyo-001&orientation=landscape"
            alt="Tokyo home with solar panels"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-32">
          <div className="max-w-2xl">
            <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full mb-8 border border-white/30 bg-white/10 backdrop-blur-sm">
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#8BC34A' }}></span>
              <span className="text-white text-sm font-medium">東京都全域対応</span>
            </div>

            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              東京の
              <br />
              <span className="bg-gradient-to-r from-[#00BCD4] to-[#8BC34A] bg-clip-text text-transparent">
                屋根・リフォーム
              </span>
              <br />
              専門家による太陽光発電と蓄電池
            </h2>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => scrollToSection('contact')}
                className="group text-white px-8 py-4 rounded-lg text-base font-semibold transition-all shadow-xl hover:shadow-2xl flex items-center justify-center space-x-3 whitespace-nowrap cursor-pointer"
                style={{ background: 'linear-gradient(135deg, #8BC34A 0%, #00BCD4 100%)' }}
              >
                <span>無料シミュレーションを依頼</span>
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </button>
              <button
                onClick={() => scrollToSection('advantage')}
                className="bg-white/15 backdrop-blur-sm text-white border border-white/30 px-8 py-4 rounded-lg text-base font-semibold hover:bg-white/25 transition-all flex items-center justify-center whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-circle-line mr-2"></i>
                詳しく見る
              </button>
            </div>

            <div className="flex items-center space-x-8 mt-12">
              <div className="flex items-center space-x-2">
                <div className="flex -space-x-1">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <i key={s} className="ri-star-fill text-yellow-400"></i>
                  ))}
                </div>
                <span className="text-white/80 text-sm">Google評価4.8</span>
              </div>
              <div className="h-4 w-px bg-white/30"></div>
              <span className="text-white/80 text-sm">148件以上の口コミ実績</span>
              <div className="h-4 w-px bg-white/30"></div>
              <span className="text-white/80 text-sm">10年以上の経験</span>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <button onClick={() => scrollToSection('advantage')} className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm cursor-pointer">
            <i className="ri-arrow-down-line text-white text-xl"></i>
          </button>
        </div>
      </section>

      {/* The Magokoro Advantage Section */}
      <section id="advantage" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-4" style={{ background: '#E8F5E9', color: '#558B2F' }}>
              まごころの強み
            </span>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              なぜ太陽光設置に<span style={{ color: '#8BC34A' }}>屋根リフォーム会社</span>を選ぶのか？
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              一般的な太陽光業者とは異なり、私たちが一番屋根を理解している専門家だからです。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {advantages.map((adv, idx) => (
              <div
                key={idx}
                className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-transparent relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-10 -translate-y-1/2 translate-x-1/2" style={{ background: 'linear-gradient(135deg, #00BCD4 0%, #8BC34A 100%)' }}></div>
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110" style={{ background: 'linear-gradient(135deg, #00BCD4 0%, #8BC34A 100%)' }}>
                  <i className={`${adv.icon} text-white text-2xl`}></i>
                </div>
                <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#8BC34A' }}>{adv.subtitle}</p>
                <h4 className="text-xl font-bold text-gray-900 mb-3">{adv.title}</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{adv.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="p-10 lg:p-12">
                <h4 className="text-2xl font-bold text-gray-900 mb-4">
                  屋根の専門家 = <br /><span style={{ color: '#8BC34A' }}>雨漏り防止</span>
                </h4>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  太陽光発電の設置と屋根リフォーム・外壁塗装を同時に行うことで、工事を一括管理。
                  足場の二重設置や工程の重複を防ぎ、工期を短縮しながらコストも大幅に削減できます。
                  無駄のない統合施工で、住宅の資産価値と省エネ性能を同時に最大化します。
                </p>
                <ul className="space-y-3">
                  {[
                    '足場一度施工によるコスト最適化\n二重工事を防ぎ、無駄な足場代を削減します。',
                    '屋根構造を踏まえた安全な太陽光設置\n下地・防水層まで理解した施工でリスクを最小化。',
                    '施工起因の雨漏り10年責任保証。\n万が一にも備えた安心のアフター体制。',
                    '数値根拠に基づく発電シミュレーション提案\n感覚ではなく、データで判断できる見積もりをご提示。',
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: '#E8F5E9' }}>
                        <i className="ri-check-line text-xs" style={{ color: '#8BC34A' }}></i>
                      </div>
                      <span className="text-gray-700 text-sm whitespace-pre-line">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative h-64 lg:h-auto">
                <img
                  src="https://static.readdy.ai/image/433d85233a05655d3e1b986bbaebb8d0/dd535821a78d6d0ada71b9044bda9d6b.jpeg"
                  alt="Expert roof installation"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Simulation Section */}
      <section id="simulation" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-4" style={{ background: '#E1F5FE', color: '#0277BD' }}>
                精密シミュレーション
              </span>
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                <span style={{ color: '#00BCD4' }}>エネがえる</span>による
                <br />
                高精度シミュレーション
              </h3>
              <p className="text-gray-600 mb-8 leading-relaxed">
                業界最高水準の「ENEがえる」シミュレーション技術を使用。
                お客様の屋根形状、日照条件、電気使用量を詳細に分析し、
                最適な太陽光発電システムをご提案します。
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { icon: 'ri-sun-line', title: '日照シミュレーション', desc: '年間を通じた発電量を正確に予測' },
                  { icon: 'ri-money-dollar-circle-line', title: '経済効果分析', desc: '投資回収期間と長期的な節約額を算出' },
                  { icon: 'ri-line-chart-line', title: '最適システム設計', desc: 'お客様のニーズに合わせたカスタマイズ提案' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start space-x-4 p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #00BCD4 0%, #8BC34A 100%)' }}>
                      <i className={`${item.icon} text-white text-xl`}></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-1">{item.title}</h5>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={() => scrollToSection('contact')}
                className="text-white px-8 py-4 rounded-lg text-base font-semibold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                style={{ background: 'linear-gradient(135deg, #00BCD4 0%, #8BC34A 100%)' }}
              >
                無料シミュレーションを依頼
              </button>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://static.readdy.ai/image/433d85233a05655d3e1b986bbaebb8d0/14fed1a40d6ced58f58dc2d50f0bd1d2.png"
                  alt="Simulation software"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-6 max-w-xs">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: '#E8F5E9' }}>
                    <i className="ri-check-line" style={{ color: '#8BC34A' }}></i>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">約90%</p>
                    <p className="text-xs text-gray-600">シミュレーション精度</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted Brands Section */}
      <section id="brands" className="py-24" style={{ background: 'linear-gradient(180deg, #F5F5F5 0%, #FFFFFF 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-4" style={{ background: '#E8F5E9', color: '#558B2F' }}>
              信頼のメーカー
            </span>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              信頼の<span style={{ color: '#8BC34A' }}>一流メーカー</span>を<br />取り扱い
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              お客様のニーズと予算に合わせて、最適なメーカーをご提案します。
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            {manufacturers.map((brand, idx) => (
              <div
                key={idx}
                className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-all duration-300 flex items-center justify-center border border-gray-100"
              >
                <img src={brand.logo} alt={brand.name} className="max-w-full h-16 object-contain grayscale hover:grayscale-0 transition-all" />
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <h4 className="text-2xl font-bold text-gray-900 mb-6 text-center">私たちの約束</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#E8F5E9' }}>
                  <i className="ri-heart-line text-xl" style={{ color: '#8BC34A' }}></i>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2">誠実な提案</h5>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    お住まいに適さないと判断した場合は、正直に「おすすめしません」とお伝えします。利益よりも、長く続く信頼を大切にします。無理な提案はしません。
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#E1F5FE' }}>
                  <i className="ri-shield-check-line text-xl" style={{ color: '#00BCD4' }}></i>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2">長期保証</h5>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    施工後も安心していただけるよう、充実したアフターサポートと長期保証をご提供します。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hybrid Plan Section */}
      <section id="hybrid" className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="rounded-3xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #8BC34A 0%, #00BCD4 100%)' }}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              {/* Left Content */}
              <div className="p-10 lg:p-14">
                <span className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-6 bg-white/20 text-white">
                  ハイブリッドソリューション
                </span>
                
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-2 leading-tight">
                  太陽光&amp;蓄電池＋屋根外壁リフォーム
                </h3>
                <h4 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: '#FFD54F' }}>
                  同時施工で最大10%節約
                </h4>
                
                <p className="text-white/90 mb-10 leading-relaxed text-sm">
                  太陽光パネル設置と屋根リフォームを同時に行うことで、足場費用を一本化し、最大限のコスト効率を実現します。統合されたアプローチにより、工期短縮と大幅な節約が可能です。
                </p>

                {/* Discount List */}
                <div className="space-y-4 mb-10">
                  <div className="flex items-center justify-between bg-white/95 rounded-xl px-5 py-4 shadow-md">
                    <span className="text-gray-800 font-medium text-sm">太陽光&蓄電池 ＋ 屋根塗装</span>
                    <span className="px-4 py-1.5 rounded-full text-sm font-bold text-white whitespace-nowrap" style={{ background: 'linear-gradient(135deg, #8BC34A 0%, #00BCD4 100%)' }}>
                      5%オフ
                    </span>
                  </div>
                  <div className="flex items-center justify-between bg-white/95 rounded-xl px-5 py-4 shadow-md">
                    <span className="text-gray-800 font-medium text-sm">太陽光&蓄電池 ＋ 屋根カバー工法</span>
                    <span className="px-4 py-1.5 rounded-full text-sm font-bold text-white whitespace-nowrap" style={{ background: 'linear-gradient(135deg, #8BC34A 0%, #00BCD4 100%)' }}>
                      8%オフ
                    </span>
                  </div>
                  <div className="flex items-center justify-between bg-white/95 rounded-xl px-5 py-4 shadow-md">
                    <span className="text-gray-800 font-medium text-sm">太陽光&蓄電池 ＋ 屋根カバー工法&外壁塗装</span>
                    <span className="px-4 py-1.5 rounded-full text-sm font-bold text-white whitespace-nowrap" style={{ background: 'linear-gradient(135deg, #E91E63 0%, #FF9800 100%)' }}>
                      10%オフ
                    </span>
                  </div>
                </div>

                {/* CTA Button */}
                <button
                  onClick={() => scrollToSection('contact')}
                  className="bg-white text-gray-800 px-8 py-4 rounded-xl text-base font-semibold hover:bg-gray-100 transition-all shadow-lg whitespace-nowrap cursor-pointer"
                >
                  ハイブリッド見積りを取得
                </button>
              </div>

              {/* Right Image */}
              <div className="relative h-80 lg:h-auto min-h-[400px]">
                {/* Before/After Images */}
                <div className="absolute inset-0 flex items-center justify-center p-8">
                  <div className="relative w-full h-full max-w-lg">
                    {/* Before Image (Background) */}
                    <div className="absolute top-0 left-0 w-3/4 h-3/4 rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
                      <img
                        src="https://readdy.ai/api/search-image?query=beautiful%20Japanese%20residential%20house%20with%20old%20weathered%20roof%20tiles%20needing%20renovation%2C%20traditional%20architecture%2C%20sunny%20day%2C%20green%20garden%2C%20before%20renovation%20state%2C%20professional%20real%20estate%20photography&width=600&height=450&seq=hybrid-before-001&orientation=landscape"
                        alt="Before renovation"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    {/* After Image (Foreground) */}
                    <div className="absolute bottom-0 right-0 w-3/4 h-3/4 rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
                      <img
                        src="https://readdy.ai/api/search-image?query=modern%20Japanese%20residential%20house%20with%20brand%20new%20solar%20panels%20installed%20on%20clean%20renovated%20roof%2C%20bright%20sunny%20day%2C%20well%20maintained%20garden%2C%20after%20renovation%20state%2C%20professional%20real%20estate%20photography&width=600&height=450&seq=hybrid-after-001&orientation=landscape"
                        alt="After renovation with solar panels"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                </div>

                {/* Savings Badge */}
                <div className="absolute bottom-8 right-8 bg-white rounded-2xl shadow-xl p-5 flex items-center space-x-4">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #8BC34A 0%, #00BCD4 100%)' }}>
                    <i className="ri-percent-line text-white text-2xl"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium">最大限節約</p>
                    <p className="text-3xl font-bold" style={{ color: '#8BC34A' }}>10%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Campaign Section */}
      <section id="campaign" className="py-24" style={{ background: 'linear-gradient(180deg, #F5F5F5 0%, #FFFFFF 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <img 
              src="https://static.readdy.ai/image/433d85233a05655d3e1b986bbaebb8d0/deee73db4e01e9f5f88851bda7508857.png" 
              alt="特別キャンペーン" 
              className="w-full h-auto rounded-2xl shadow-xl"
            />
          </div>

          <div className="mt-12 text-center">
            <button
              onClick={() => scrollToSection('contact')}
              className="text-white px-10 py-4 rounded-lg text-base font-semibold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              style={{ background: 'linear-gradient(135deg, #E91E63 0%, #FF9800 100%)' }}
            >
              今すぐ無料相談を予約
            </button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-4" style={{ background: '#FFF9C4', color: '#F57F17' }}>
              お客様の声
            </span>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              お客様の<span style={{ color: '#FFC107' }}>喜びの声</span>
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              実際にまごころ工務店をご利用いただいたお客様からの声をご紹介します。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((test, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border border-gray-100">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
                    <img src={test.image} alt={test.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900">{test.name}</h5>
                    <p className="text-sm text-gray-500">{test.location}</p>
                  </div>
                </div>
                <div className="flex mb-4">
                  {[...Array(test.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400"></i>
                  ))}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed italic">"{test.quote}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-24" style={{ background: 'linear-gradient(180deg, #F5F5F5 0%, #FFFFFF 100%)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-4" style={{ background: '#E8F5E9', color: '#558B2F' }}>
              お問い合わせ
            </span>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              無料シミュレーション<span style={{ color: '#8BC34A' }}>お申し込み</span>
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              まずは無料でシミュレーションを実施します。お気軽にお問い合わせください。
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#8BC34A] focus:ring-2 focus:ring-[#8BC34A]/20 outline-none transition-all text-sm"
                    placeholder="山田 太郎"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#8BC34A] focus:ring-2 focus:ring-[#8BC34A]/20 outline-none transition-all text-sm"
                    placeholder="example@email.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    電話番号 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#8BC34A] focus:ring-2 focus:ring-[#8BC34A]/20 outline-none transition-all text-sm"
                    placeholder="090-1234-5678"
                  />
                </div>
                <div>
                  <label htmlFor="address" className="block text-sm font-semibold text-gray-700 mb-2">
                    ご住所 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#8BC34A] focus:ring-2 focus:ring-[#8BC34A]/20 outline-none transition-all text-sm"
                    placeholder="東京都新宿区西新宿5-5-1-5007号"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  ご要望・ご質問
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#8BC34A] focus:ring-2 focus:ring-[#8BC34A]/20 outline-none transition-all text-sm resize-none"
                  placeholder="ご要望やご質問がございましたら、こちらにご記入ください（500文字以内）"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={submitStatus === 'sending'}
                className="w-full text-white px-8 py-4 rounded-lg text-base font-semibold transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                style={{ background: 'linear-gradient(135deg, #8BC34A 0%, #00BCD4 100%)' }}
              >
                {submitStatus === 'sending' ? '送信中...' : '無料シミュレーションを申し込む'}
              </button>

              {submitStatus === 'success' && (
                <div className="mt-4 p-4 rounded-lg flex items-center space-x-3" style={{ background: '#E8F5E9' }}>
                  <i className="ri-check-circle-line text-xl" style={{ color: '#8BC34A' }}></i>
                  <span className="text-sm font-medium" style={{ color: '#558B2F' }}>
                    お申し込みありがとうございます！担当者より3営業日以内にご連絡いたします。
                  </span>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-4 p-4 rounded-lg flex items-center space-x-3" style={{ background: '#FFEBEE' }}>
                  <i className="ri-error-warning-line text-xl" style={{ color: '#C62828' }}></i>
                  <span className="text-sm font-medium" style={{ color: '#C62828' }}>
                    送信に失敗しました。もう一度お試しください。
                  </span>
                </div>
              )}
            </form>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: 'ri-phone-line', title: 'お電話でのお問い合わせ', info: '0120-69-9140', sub: '平日 9:00-19:00' },
              { icon: 'ri-mail-line', title: 'メールでのお問い合わせ', info: 'info@yu-iiyane.com', sub: '24時間受付' },
              { icon: 'ri-map-pin-line', title: '本社所在地', info: '東京都新宿区西新宿5-5-1-5007号', sub: '東京都全域対応' },
            ].map((contact, idx) => (
              <div key={idx} className="bg-white rounded-xl p-6 shadow-md text-center">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: '#E8F5E9' }}>
                  <i className={`${contact.icon} text-xl`} style={{ color: '#8BC34A' }}></i>
                </div>
                <h5 className="font-semibold text-gray-900 text-sm mb-2">{contact.title}</h5>
                <p className="text-gray-900 font-medium mb-1">{contact.info}</p>
                <p className="text-xs text-gray-500">{contact.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16" style={{ background: 'linear-gradient(135deg, #8BC34A 0%, #00BCD4 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <img
                src="https://static.readdy.ai/image/433d85233a05655d3e1b986bbaebb8d0/93db42b0d420b42c689b29e03900a394.png"
                alt="まごころ工務店"
                className="h-12 mb-4"
              />
              <p className="text-white/90 text-sm leading-relaxed mb-4">
                東京都全域で太陽光発電と屋根リフォームを提供する専門会社です。
                雨漏りゼロ品質と誠実な提案で、お客様の信頼にお応えします。
              </p>
              <div className="flex space-x-4">
                {['ri-facebook-circle-line', 'ri-twitter-x-line', 'ri-instagram-line', 'ri-youtube-line'].map((icon, idx) => (
                  <button key={idx} className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-all cursor-pointer">
                    <i className={`${icon} text-white text-lg`}></i>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h5 className="text-white font-semibold mb-4">サービス</h5>
              <ul className="space-y-2">
                {['太陽光発電設置', '屋根リフォーム', '外壁塗装', 'ハイブリッドプラン'].map((item, idx) => (
                  <li key={idx}>
                    <button className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">{item}</button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h5 className="text-white font-semibold mb-4">会社情報</h5>
              <ul className="space-y-2">
                {['会社概要', '施工実績', 'お客様の声', 'よくある質問'].map((item, idx) => (
                  <li key={idx}>
                    <button className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">{item}</button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-white/80 text-sm">© 2025 まごころ工務店. All rights reserved.</p>
            <div className="flex items-center space-x-6">
              <button className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">プライバシーポリシー</button>
              <button className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">利用規約</button>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white text-sm transition-colors">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}